import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hd2:currentLoadout:v2";
const LEGACY_STORAGE_KEY = "hd2:currentLoadout";
const CHANGE_EVENT = "hd2:currentLoadoutChange";

export type LoadoutSlotKey =
	| "primary"
	| "secondary"
	| "grenade"
	| "armor"
	| "stratagems"
	| "booster";

export interface CurrentLoadout {
	version: 2;
	primary: string | null;
	secondary: string | null;
	grenade: string | null;
	armor: string | null;
	stratagems: [string | null, string | null, string | null, string | null];
	booster: string | null;
}

const EMPTY_LOADOUT: CurrentLoadout = {
	version: 2,
	primary: null,
	secondary: null,
	grenade: null,
	armor: null,
	stratagems: [null, null, null, null],
	booster: null,
};

function read(): CurrentLoadout {
	localStorage.removeItem(LEGACY_STORAGE_KEY);
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return { ...EMPTY_LOADOUT, stratagems: [...EMPTY_LOADOUT.stratagems] };
	}
	try {
		const parsed = JSON.parse(raw) as CurrentLoadout;
		if (parsed.version !== 2) {
			return { ...EMPTY_LOADOUT, stratagems: [...EMPTY_LOADOUT.stratagems] };
		}
		return parsed;
	} catch {
		return { ...EMPTY_LOADOUT, stratagems: [...EMPTY_LOADOUT.stratagems] };
	}
}

function write(loadout: CurrentLoadout) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(loadout));
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

export interface UseCurrentLoadoutResult {
	loadout: CurrentLoadout;
	equip(slot: LoadoutSlotKey, slug: string, stratagemIndex?: number): void;
	clear(slot: LoadoutSlotKey, stratagemIndex?: number): void;
	clearAll(): void;
	setAll(next: CurrentLoadout): void;
}

export function useCurrentLoadout(): UseCurrentLoadoutResult {
	const [loadout, setLoadout] = useState<CurrentLoadout>(() => read());

	useEffect(() => {
		const sync = () => {
			setLoadout(read());
		};
		window.addEventListener("storage", sync);
		window.addEventListener(CHANGE_EVENT, sync);
		return () => {
			window.removeEventListener("storage", sync);
			window.removeEventListener(CHANGE_EVENT, sync);
		};
	}, []);

	const equip = useCallback(
		(slot: LoadoutSlotKey, slug: string, stratagemIndex?: number) => {
			const current = read();
			if (slot === "stratagems") {
				if (current.stratagems.includes(slug)) {
					return;
				}
				const strats = [...current.stratagems] as CurrentLoadout["stratagems"];
				if (stratagemIndex !== undefined) {
					strats[stratagemIndex] = slug;
				} else {
					const emptyIdx = strats.indexOf(null);
					if (emptyIdx !== -1) {
						strats[emptyIdx] = slug;
					} else {
						// All full, no index given — caller should open the slot picker instead
						return;
					}
				}
				write({ ...current, stratagems: strats });
			} else {
				write({ ...current, [slot]: slug });
			}
		},
		[],
	);

	const clear = useCallback((slot: LoadoutSlotKey, stratagemIndex?: number) => {
		const current = read();
		if (slot === "stratagems" && stratagemIndex !== undefined) {
			const strats = [...current.stratagems] as CurrentLoadout["stratagems"];
			strats[stratagemIndex] = null;
			write({ ...current, stratagems: strats });
		} else {
			write({
				...current,
				[slot]: slot === "stratagems" ? [null, null, null, null] : null,
			});
		}
	}, []);

	const clearAll = useCallback(() => {
		write({ ...EMPTY_LOADOUT, stratagems: [...EMPTY_LOADOUT.stratagems] });
	}, []);

	const setAll = useCallback((next: CurrentLoadout) => {
		write({ ...next, stratagems: [...next.stratagems] });
	}, []);

	return { loadout, equip, clear, clearAll, setAll };
}
